"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "@heroicons/react/24/outline";

import cx from "@/utils/cx";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

interface CheckboxProps<T extends FieldValues>
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  label?: React.ReactNode;
  name: Path<T>;
  control: Control<T>;
  required?: boolean;
  defaultChecked?: boolean;
  labelClassName?: string;
  containerClassName?: string;
}

function Checkbox<T extends FieldValues>({
  className,
  label,
  control,
  required,
  name,
  defaultChecked,
  containerClassName,
  labelClassName,
  ...props
}: CheckboxProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={{ required }}
      render={({
        field: { onChange, onBlur, value, name, ref },
        fieldState: { invalid, isTouched, isDirty, error },
      }) => {
        return (
          <div className="grid gap-1">
            <div
              className={cx("items-center flex space-x-2", containerClassName)}
            >
              <CheckboxPrimitive.Root
                ref={ref}
                className={cx(
                  "my-1 peer h-4 w-4 shrink-0 rounded-sm border border-primary shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-white",
                  className
                )}
                aria-label="checkbox"
                name={name}
                checked={value}
                onBlur={onBlur}
                onCheckedChange={onChange}
                defaultChecked={defaultChecked}
                id={name}
                {...props}
              >
                <CheckboxPrimitive.Indicator
                  className={cx(
                    "flex items-center justify-center text-current"
                  )}
                >
                  <CheckIcon className="h-3 w-3" />
                </CheckboxPrimitive.Indicator>
              </CheckboxPrimitive.Root>

              {label && (
                <label
                  htmlFor={name}
                  className={cx(
                    "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
                    labelClassName
                  )}
                >
                  {label}
                </label>
              )}
            </div>
            {error && <p className="text-red-400 text-xs">{error.message}</p>}
          </div>
        );
      }}
    />
  );
}

export default Checkbox;
